import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import {
  CompositeMeasureEditor,
  CompositeMeasuresPanel,
  EMPTY_TOTAL_WARNING,
  LINK_FLASH_MS,
  ownCamerasReason,
  ROW_MOTION_ARRIVED_MS,
  rowState,
  STATE_CHIP,
} from '@repo/ui';
import type {
  CompositeMeasureEditorProps,
  CompositeRow,
  CompositeRowState,
  EditorNote,
  EditorSource,
  PickerCandidate,
} from '@repo/ui';
import { Banner, Button, Icon, Modal, Tag } from '@repo/design-system';
import { ink, space, typeScale } from '@repo/design-tokens';
import { asArray, generationTargets, isLive, newCompositeRefid } from '@repo/front-end-utils';
import type {
  FloorPlannerComposite,
  FloorPlannerCompositeRef,
  FloorPlannerLayout,
} from '@repo/frontend-types';

import { decodeRef, encodeRef, labelForRef } from './compositeRefOptions';
import { automaticTotals } from './entityCoverage';
import { LIVE_COMPOSITE_MARKER } from './matchCompositesToPlan';
import type { LiveTwinConfiguration } from './useLiveTwinConfiguration';
import {
  compositeVerdictContext,
  planCompositeVerdicts,
  sourceStates,
  targetKindOf,
  sourceLookups,
  sourcePickerCandidates,
  targetPickerCandidates,
  totalVerdicts,
  targetPickOutcome,
  wouldLoop,
} from './compositeVerdicts';
import {
  automaticRowRefid,
  fieldsOf,
  ownCamerasState,
  prefillFor,
  rowTargetKey,
  sameDefinition,
  sameStored,
  sourceActionsFor,
  withExcluded,
  withInputAdded,
  withInputRemoved,
  withOwnCameras,
  withTarget,
} from './compositeEditing';
import { generatedReviews } from './parentSpaceReview';
import { CompositeTotalSearch } from './CompositeTotalSearch';
import {
  compositeRefEntries,
  compositeTotalRefs,
  matchCompositeTotals,
  totalsReadState,
} from './compositeSearch';
import type { CompositeSearchHit } from './compositeSearch';
import { useJoinedPlanSearchIndex } from './usePlanSearchSources';

/** What the title-bar search indexes from the plan; the floors and the twin are added to it. */
const COMPOSITE_SEARCH_KINDS = ['surface', 'item'] as const;

/** What R12 says when the open total has left the plan. One sentence, every path that says it. */
const goneSentence = (label: string) =>
  `${label} is no longer on this plan, so nothing was saved. It may have been replaced by the ` +
  'twin’s own version while this was open.';

/** What R12 says when the open total was rewritten underneath the panel. */
const changedSentence = (label: string) =>
  `The total for ${label} changed while this was open, so nothing was saved. Check it before ` +
  'changing it again.';

/**
 * **Composite measures** — the wizard's sixth stage, between Observers and Launch
 * (`complex-measures-in-configuration-composites-stage`, epic ledger rows 13–15).
 *
 * WHY IT MOVED. It used to sit AFTER Launch, because the wizard had no configuration before Go
 * live: observers went into the plan JSON and the configuration was minted inside `handleGoLive`.
 * So the stage targeted the twin's ACTIVE configuration and wrote to it directly — three reads,
 * four writes, and a reconciliation layer for every one of them, because the write target was
 * live. Complex measures are configuration, and configuration is what the plan describes, so the
 * stage now edits `twin_plan.layout.composites` and Go Live files it.
 *
 * WHAT THAT DELETED. Every middleware call; the build button and the duplicate-name guard behind
 * it; the "cannot open for edit" refusal; the nav lock; the three outcome states; and the
 * unknown-outcome sentences each write needed. None of it was rewritten — a store write cannot
 * half-succeed, so there is nothing to reconcile.
 *
 * WHAT IT DOES NOT DO YET. Nothing here files anything: Go Live still files observers only until
 * `go-live-statement` lands in the same release. The banner says so, and it is the honest
 * statement of what the plan means rather than a claim about today.
 *
 * IDENTITY. A member is a plan `refid`, never a name. Two entities called "Rack 6" are two options
 * told apart by their entity codes, which is why the name guards went rather than being ported.
 */

/** The one empty composites list — see `stableComposites`. */
const NO_COMPOSITES: FloorPlannerComposite[] = [];

interface Props {
  className?: string;
  /** The plan's complex measures, tombstones included — the stage filters and writes them back. */
  composites: FloorPlannerComposite[];
  /** The session layout, for the picker's options and the row labels. */
  layout: FloorPlannerLayout | null;
  twinBid: string | undefined;
  twinName?: string;
  /**
   * The twin's numeric id — what the register read is keyed by, so the title-bar search repairs a
   * stale machine link only on a read that succeeded (`useJoinedPlanSearchIndex`).
   */
  twinId?: number | null;
  /**
   * Where the title-bar search goes (`composites-page-search`, spec FR4): an element the page puts
   * first in its title bar. The step portals its box into it, because the totals it searches and
   * what a pick does are the step's. Absent, no search box renders.
   */
  searchSlot?: HTMLElement | null;
  /** Replace the plan's whole `composites` section. Wired to `FloorplanStore.setComposites`. */
  onChange: (next: FloorPlannerComposite[]) => void;
  /**
   * The plan's composites as they stand RIGHT NOW, read from the store rather than from a render.
   *
   * Needed by any handler that awaits before writing — the delete confirmation is an unbounded
   * wait on a human, and the load can replace this section while it is open.
   */
  latestComposites: () => FloorPlannerComposite[];
  /**
   * The page's live-configuration read, for the state of the load that puts the twin's OWN complex
   * measures into this list.
   *
   * Optional so a host that does not read the configuration renders exactly as before — but the
   * wizard always passes it, because without it this stage renders a failed read and a twin that
   * measures nothing identically, which is the conflation the whole live-read feature exists to
   * remove. `LiveConfigurationNotices` is not reused here: every sentence it carries is about
   * observers and camera streams, and neither is what this stage shows.
   */
  liveConfiguration?: LiveRead;
  /**
   * The page's generated parent-space totals (`complex-measures-in-configuration-parent-space-
   * composites`). The page reconciles them; the stage only reports on them.
   *
   * - `paused` — the twin's running configuration could not be read, so the page is NOT generating:
   *   generating over a configuration whose own totals are unknown would put a second total beside
   *   one of them.
   * - `added` — the targets the session's FIRST reconciliation added, to be announced once; `null`
   *   when there is nothing to announce. `onAddedAnnounced` is called once the stage has said it.
   */
  generation?: {
    paused: boolean;
    /** The page is generating right now: the configuration has settled and was read. */
    active?: boolean;
    added: FloorPlannerCompositeRef[] | null;
    onAddedAnnounced: () => void;
  };
}

type LiveRead = Pick<LiveTwinConfiguration, 'config' | 'statement' | 'compositeMatch' | 'retry'>;

/*
 * `storedShape`, `sameDefinition` and `sameStored` moved to `compositeEditing.ts`
 * (`composites-page-editor`): every editor action now builds its stored row there, and the host
 * applies it to the row as it stands at write time.
 */

/**
 * What the live read has to say about the twin's OWN complex measures.
 *
 * The list on this stage holds two kinds of measure — drawn here, and loaded from the running
 * twin's configuration — and they are indistinguishable once placed. So a read that failed and a
 * twin that genuinely measures nothing produce the same empty list, and the next thing the operator
 * does is press Go Live on a twin whose existing measures were never loaded and are about to be
 * replaced by what is on screen. Saying which of the two happened is the point.
 *
 * Every sentence names the severity in words rather than leaning on the banner's tone (WCAG 1.4.1),
 * and each Retry carries an accessible name saying what it retries — the same rules
 * `LiveConfigurationNotices` follows, applied to this stage's own subject rather than to observers.
 * That component is not reused: every sentence in it is about observers and camera streams.
 */
/** How many unplaceable measures are named before the rest become a count — as on the Observers
 * stage, whose notices name three and then count. */
const UNPLACED_NAMED_LIMIT = 3;

const LiveReadNotices: React.FC<LiveRead> = ({ config, statement, compositeMatch, retry }) => {
  /*
   * "Reading…" only while a read is in flight AND neither has failed. When the configuration read
   * fails the statement read never starts, so it sits in `loading` for ever — and the notice used
   * to say "Reading…" beside the failure banner (backlog: seen on staging GMN-MCT;
   * `composites-page-list`, spec R17).
   */
  const loading =
    (config.status === 'loading' || statement.status === 'loading') &&
    config.status !== 'failed' &&
    statement.status !== 'failed';
  /*
   * ⚠ Withheld unless BOTH reads are ready, and the entries are OBJECTS.
   *
   * Two defects lived in one line here. `unplaced` is `UnplacedComposite[]` —
   * `{compositeId, name, reason}` — not a list of names, so interpolating it rendered
   * `[object Object]` to the operator. And the hook only ever writes `compositeMatch` on a
   * successful match, so a retry that FAILS leaves the previous configuration's list standing:
   * rendering it beside "could not read" is the contradiction `LiveConfigurationNotices` states
   * in its own header — a count from a previous attempt sitting under a failure banner.
   */
  const ready = config.status === 'ready' && statement.status === 'ready';
  const unplaced = ready && compositeMatch ? compositeMatch.unplaced : [];
  const named = unplaced.slice(0, UNPLACED_NAMED_LIMIT);

  return (
    <div className="live-read-notices">
      {loading && (
        <Banner
          surface="on-glass"
          tone="info"
          role="status"
          title="Reading this twin&rsquo;s own composite measures&hellip;"
        />
      )}

      {config.status === 'failed' && (
        <Banner
          surface="on-glass"
          tone="error"
          title="Could not read which of this twin&rsquo;s configurations is active."
        >
          {/*
           * NOT `config.reason`. That is the shared `REASON.config` sentence — "Could not read this
           * twin's observer configuration." — written for the Observers stage, and on a stage about
           * complex measures it names the wrong subject entirely. The retry is named for what it
           * actually re-runs, which is the configuration list and not the observer read.
           */}
          None of this twin&rsquo;s own composite measures have been loaded, so this list is what
          the plan holds and not what the twin measures today.{' '}
          <Button
            type="button"
            variant="ghost"
            size="xs"
            aria-label="Retry reading which configuration is active"
            onClick={() => retry('config')}
          >
            Retry
          </Button>
        </Banner>
      )}

      {statement.status === 'failed' && (
        <Banner surface="on-glass" tone="error" title={statement.reason}>
          None of this twin&rsquo;s own composite measures have been loaded. This is a failed read,
          not a twin that measures nothing.{' '}
          <Button
            type="button"
            variant="ghost"
            size="xs"
            aria-label="Retry reading the configuration statement"
            onClick={() => retry('observers')}
          >
            Retry
          </Button>
        </Banner>
      )}

      {config.status === 'none' && (
        <Banner
          surface="on-glass"
          tone="info"
          role="status"
          title="This twin has no configuration yet, so it has no composite measures of its own."
        >
          Everything below was drawn on the plan.
        </Banner>
      )}

      {unplaced.length > 0 && (
        <Banner
          surface="on-glass"
          tone="warning"
          title={`${unplaced.length} of this twin's composite measures could not be placed on the plan.`}
        >
          {/*
           * Each one carries the matcher's OWN reason, rather than a single sentence over all of
           * them. "Totals something this plan does not have" is only one of the reasons it
           * produces: a member the server could not resolve, a measure that adds up nothing, and a
           * floor this plan has more than one of are the others, and telling an operator the wrong
           * one sends them to look in the wrong place.
           */}
          They are not shown below and they are not in the plan:
          <ul className="unplaced-list">
            {named.map(u => (
              <li key={u.compositeId}>{u.reason}</li>
            ))}
          </ul>
          {unplaced.length > named.length ? `…and ${unplaced.length - named.length} more.` : null}
        </Banner>
      )}
    </div>
  );
};

/** A written row's *Edit* button — where focus goes back to that row. */
const editButtonIn = (root: HTMLElement | null, refid: string): HTMLElement | null =>
  root?.querySelector<HTMLElement>(
    `[data-refid="${CSS.escape(refid)}"] button[aria-label^="Edit the "]`,
  ) ?? null;

/** The step's *Add composite measure* button — focus's fallback when the thing it was on has gone. */
const addButtonIn = (root: HTMLElement | null): HTMLButtonElement | null =>
  Array.from(root?.querySelectorAll<HTMLButtonElement>('button') ?? []).find(
    b => b.textContent?.trim() === 'Add composite measure',
  ) ?? null;

const OnboardingCompositeMeasures: React.FC<Props> = ({
  className,
  composites,
  layout,
  twinBid,
  twinName,
  twinId,
  searchSlot,
  onChange: writeComposites,
  latestComposites,
  liveConfiguration,
  generation,
}) => {
  /*
   * Every write this step makes goes through here, so the step knows which plan arrays are its OWN
   * (`composites-page-motion`, spec R3): the store hands the written array back unchanged, and any
   * other array is a rebuild — the live configuration's match, the page's reconciliation, a reload.
   */
  const ownWrite = useRef<FloorPlannerComposite[] | null>(null);
  const onChange = useCallback(
    (next: FloorPlannerComposite[]) => {
      ownWrite.current = next;
      writeComposites(next);
    },
    [writeComposites],
  );
  /*
   * The editor session (`composites-page-editor`, spec R12). The panel renders the stored total and
   * holds no copy of it; this is everything the HOST needs to apply each action safely:
   *
   * - `opened` — the row as it was when the session opened, for Revert (`null` for Add).
   * - `lastWritten` — the row as this session last wrote it. Every change first checks the stored
   *   row still says this; if the page's reconciliation or a background load rewrote it meanwhile,
   *   the change is refused rather than written over the newer total. Measured against the LAST
   *   WRITE, never against `opened`, or the second change of a session would be refused.
   * - `n` — bumped each time the panel opens or moves to another total, so focus is placed once.
   */
  const [session, setSession] = useState<{
    mode: 'add' | 'edit';
    refid: string | null;
    opened: FloorPlannerComposite | null;
    lastWritten: FloorPlannerComposite | null;
    replacedAutomatic: boolean;
    prefilled: boolean;
    focusOnOpen: 'region' | 'target' | 'none';
    n: number;
    /**
     * This session has written since it opened (or since its last refusal). Revert is offered only
     * then (spec R3): a total rewritten underneath by something else is not this session's to undo.
     */
    wrote: boolean;
  } | null>(null);
  /** Read by the action handlers, which must see the session as it is now, not as a render saw it. */
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const sessionCount = useRef(0);
  /**
   * The Edit session's total, for focus return. Kept apart from the session because the panel's
   * focus return runs AFTER `closeSession` has cleared the session (and `sessionRef` with it).
   */
  const returnRefid = useRef<string | null>(null);
  /** The card that just arrived (its fading glow) and the row that reads *Added* (spec R8, R17). */
  const [arrival, setArrival] = useState<{ card: string; row: string | null } | null>(null);
  /**
   * A row to glow for a moment (`composites-page-motion`, spec R6, R7): a total the operator has
   * just added, or one Reset has put back, both in the just-changed mint
   * (`onboarding-status-colours-lists`). As long as a moved row glows.
   */
  const [rowGlow, setRowGlow] = useState<{ refid: string; tone: 'new' | 'arrived' } | null>(null);
  useEffect(() => {
    if (!rowGlow) return;
    const timer = window.setTimeout(() => setRowGlow(null), ROW_MOTION_ARRIVED_MS);
    return () => window.clearTimeout(timer);
  }, [rowGlow]);
  const rootRef = useRef<HTMLDivElement>(null);
  /** After deleting a generated total that comes straight back, where focus goes next. */
  const [pendingFocus, setPendingFocus] = useState<{
    target: string;
    label: string;
    deletedRefid: string;
  } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Everything the rules need about this plan — the options, the observer counts, the containment,
   * the measures as edges — from the ONE module Launch and Go live also read (`compositeVerdicts.ts`,
   * `composites-page-launch-refusals`). The step keeps no verdict logic of its own: a second copy
   * here is how the step and Launch would come to disagree about the same total.
   */
  /*
   * ⚠ **A plan with no composites must not rebuild the context on every render.** The page passes
   * `asArray(sessionLayout?.composites)`, which is a NEW `[]` each call when the section is absent,
   * so keying the memo on that array re-walked the plan's containment on every render of a step
   * with nothing on it yet. One empty array for all of them (review of this change, 2026-10-05).
   */
  const stableComposites = composites.length > 0 ? composites : NO_COMPOSITES;
  const verdictContext = useMemo(
    () => compositeVerdictContext({ composites: stableComposites, layout, twinBid, twinName }),
    [stableComposites, layout, twinBid, twinName],
  );
  const { options, observerCounts, live, labelled, codeForRef } = verdictContext;

  /**
   * The code under a row's name (spec R9): the target's BID, or *Floor* / *Whole twin* for the two
   * kinds that have none. Absent where a placement has not been given a BID yet.
   */
  const codeOf = useCallback(
    (ref: FloorPlannerCompositeRef | undefined): string | undefined => {
      if (!ref || ref.kind === 'twin') return 'Whole twin';
      if (ref.kind === 'floor') return 'Floor';
      return codeForRef(encodeRef(ref));
    },
    [codeForRef],
  );

  /** Why each generated total needs a look — derived from the plan, never stored (FR11a). */
  const reviews = useMemo(
    () => generatedReviews(layout, ref => labelForRef(ref, options).label),
    [layout, options],
  );

  /** What is wrong with each measure the plan holds — see `planCompositeVerdicts`. */
  const verdicts = useMemo(() => planCompositeVerdicts(verdictContext), [verdictContext]);

  /*
   * ⛔ **Every total the gym holds, not only the ones a person wrote** (`refiling-preserves-
   * configuration`, epic ledger row 123). The filing derives a total for every target two or more
   * cameras watch and rebuilds it on every Go Live; the read folds those away, so this stage used to
   * list none of them — on staging PUL-CON it showed one total where the configuration held
   * fifteen, which is how a loss of two of them went unseen. Listed read-only after the written
   * ones. A target a person has written a total for gets no automatic one (the filing's own rule),
   * so it is not listed twice.
   */
  /** Every total the filing will derive from the plan's observers. Read by the rows and the chips. */
  const automatic = useMemo(() => automaticTotals(layout), [layout]);

  const automaticRows = useMemo<CompositeRow[]>(() => {
    const written = new Set(live.map(c => encodeRef(c.target)).filter(Boolean));
    return automatic
      .filter(total => !written.has(encodeRef(total.target)))
      .map(total => ({
        refid: automaticRowRefid(encodeRef(total.target)),
        target: labelForRef(total.target, options),
        kind: targetKindOf(total.target),
        code: codeOf(total.target),
        inputs: [],
        sources: [],
        automatic: { observers: total.cameras },
      }));
  }, [automatic, codeOf, live, options]);

  /** Who stands on each target — built once for the chips and the *Add source* picker (R6). */
  const lookups = useMemo(
    () => sourceLookups(verdictContext, verdicts, { automatic, reviews }),
    [automatic, reviews, verdictContext, verdicts],
  );
  /** Where each source of each total stands, from the same verdicts the rows read (spec R11). */
  const sources = useMemo(
    () => sourceStates(verdictContext, verdicts, { automatic, reviews }, lookups),
    [automatic, lookups, reviews, verdictContext, verdicts],
  );

  /**
   * Which totals would be generated again if deleted, so their delete reads as a RESET (spec R16).
   * A generated total always is. A hand-made one is when its space is one the page generates for —
   * `generationTargets`, the reconciler's own want-test without its hand-made clause — and no other
   * written total stands on the same target to keep the space covered.
   */
  const resetting = useMemo(() => {
    const wanted = generationTargets(layout);
    const onTarget = new Map<string, number>();
    for (const c of live) {
      const key = encodeRef(c.target);
      if (key) onTarget.set(key, (onTarget.get(key) ?? 0) + 1);
    }
    const out = new Set<string>();
    for (const c of live) {
      const key = encodeRef(c.target);
      if (c.generated === true || (key && wanted.has(key) && onTarget.get(key) === 1)) {
        out.add(c.refid);
      }
    }
    return out;
  }, [layout, live]);

  /*
   * What rebuilds the list rather than moving a row in it (`composites-page-motion`, spec R3): the
   * twin; the running configuration's two reads settling; and every change to the plan this step did
   * not write — the configuration's match placing observers and totals, the page's reconciliation
   * adding generated ones — each counted in the commit that brings it, which is the commit whose
   * rows change. A configuration that loads re-judges every total at once; that is not the operator
   * moving anything.
   */
  const countsKey = useMemo(
    () =>
      Array.from(observerCounts.entries())
        .map(([key, n]) => `${key}=${n}`)
        .sort()
        .join(','),
    [observerCounts],
  );
  /*
   * Read while rendering, so the count moves in the very commit that brings the rebuilt rows: a
   * plan array the step did not write, or a change in what watches what. Idempotent per input, so a
   * second render of the same commit counts nothing.
   */
  const seen = useRef({ composites: stableComposites, countsKey, count: 0 });
  if (stableComposites !== seen.current.composites || countsKey !== seen.current.countsKey) {
    const external =
      (stableComposites !== seen.current.composites && composites !== ownWrite.current) ||
      countsKey !== seen.current.countsKey;
    seen.current = {
      composites: stableComposites,
      countsKey,
      count: seen.current.count + (external ? 1 : 0),
    };
  }
  const rebuildCount = seen.current.count;
  const motionEpoch = [
    twinBid ?? '',
    liveConfiguration?.config.status ?? '',
    liveConfiguration?.statement.status ?? '',
    rebuildCount,
  ].join('|');
  const rows = useMemo<CompositeRow[]>(
    () => [
      // `labelled` is `live.map(...)` (compositeVerdicts.ts), so index i is the same measure.
      ...labelled.map((row, i) => {
        const stored = live[i];
        return {
          ...row,
          kind: targetKindOf(stored?.target),
          code: codeOf(stored?.target),
          sources: sources.get(row.refid) ?? [],
          problems: verdicts.problems.get(row.refid),
          warnings: verdicts.warnings.get(row.refid),
          ...(verdicts.nothingMeasured.has(row.refid) ? { nothingMeasured: true as const } : {}),
          review: reviews.get(row.refid),
          ...(resetting.has(row.refid) ? { resets: true as const } : {}),
        };
      }),
      ...automaticRows,
    ],
    [automaticRows, codeOf, labelled, live, resetting, reviews, sources, verdicts],
  );

  /** A written total's state as its own row shows it — what its *Total for* option says. */
  const stateOfTotal = useCallback(
    (refid: string) => {
      const row = rows.find(r => r.refid === refid);
      return row ? rowState(row) : undefined;
    },
    [rows],
  );

  /*
   * ⛔ **A `console.warn` USED TO BE HERE, AND A BROWSER CONSOLE IS NOT A REPORT.** It read:
   *
   *     useEffect(() => { for (const entry of unresolved)
   *       console.warn('[composites-stage] composite references something this plan does not
   *         have:', entry); }, [unresolved]);
   *
   * — so the only record that a measure pointed at something the plan had lost belonged to
   * whoever happened to have devtools open. It is now a sentence in the stage's banner, against
   * the measure it is about, which is what Scope Ledger row 33 asks for.
   */

  /** The total the panel is showing, as the plan holds it right now. */
  const sessionRow = useMemo(
    () => (session?.refid ? live.find(c => c.refid === session.refid) : undefined),
    [live, session?.refid],
  );

  const labelOfRef = useCallback(
    (ref: FloorPlannerCompositeRef) => labelForRef(ref, options).label,
    [options],
  );

  /** Where focus goes when the panel closes: the row's Edit button, else *Add composite measure*. */
  const editorReturnFocus = useMemo(
    () => ({
      get current(): HTMLElement | null {
        const root = rootRef.current;
        const refid = returnRefid.current;
        const edit = refid ? editButtonIn(root, refid) : null;
        return edit?.isConnected ? edit : addButtonIn(root);
      },
    }),
    [],
  );

  const openEdit = useCallback(
    (refid: string, focusOnOpen: 'region' | 'target' | 'none' = 'region') => {
      // Re-opening the total already open keeps its session — and with it, Revert's baseline.
      const open = sessionRef.current;
      if (open?.mode === 'edit' && open.refid === refid) return;
      const opened = asArray(latestComposites()).find(c => c.refid === refid && isLive(c));
      if (!opened) return;
      returnRefid.current = refid;
      sessionCount.current += 1;
      setArrival(null);
      setSession({
        mode: 'edit',
        refid,
        opened,
        lastWritten: opened,
        replacedAutomatic: false,
        prefilled: false,
        focusOnOpen,
        n: sessionCount.current,
        wrote: false,
      });
    },
    [latestComposites],
  );

  const handleAdd = useCallback(() => {
    setNotice(null);
    setError(null);
    setArrival(null);
    returnRefid.current = null;
    sessionCount.current += 1;
    setSession({
      mode: 'add',
      refid: null,
      opened: null,
      lastWritten: null,
      replacedAutomatic: false,
      prefilled: false,
      focusOnOpen: 'target',
      n: sessionCount.current,
      wrote: false,
    });
  }, []);

  const handleEdit = useCallback(
    (refid: string) => {
      setNotice(null);
      setError(null);
      openEdit(refid);
    },
    [openEdit],
  );

  /*
   * The title-bar search (`composites-page-search`, spec FR3–FR5). A hit is a TOTAL, found by its
   * own target or by any of its sources: the shared matcher finds the plan things — over the same
   * register-joined index the Observers step searches, so a machine is found by its type and
   * manufacturer here too — and `matchCompositeTotals` maps them onto the totals that use them.
   */
  /*
   * The search reads the plan's floors only. A composite write (`setComposites`) hands back a new
   * layout with the SAME floors, so keyed on the layout the join and index would be rebuilt on every
   * autosaved edit for nothing (review).
   */
  const floors = layout?.floors;
  const searchLayout = useMemo(
    () => layout,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on what the index reads
    [floors],
  );
  const searchIndex = useJoinedPlanSearchIndex(searchLayout, {
    twinBid,
    twinId,
    include: COMPOSITE_SEARCH_KINDS,
  });
  const searchModel = useMemo(() => {
    const totals = compositeTotalRefs(live, automatic);
    return {
      totals,
      entries: compositeRefEntries(searchIndex.index, { layout: searchLayout, options, totals }),
    };
  }, [automatic, live, options, searchIndex.index, searchLayout]);
  const matchTotals = useCallback(
    (query: string) =>
      matchCompositeTotals(query, {
        entries: searchModel.entries,
        totals: searchModel.totals,
        rows,
        twinBid,
      }),
    [rows, searchModel, twinBid],
  );
  /** A jump for the list to make — the chip's own jump, asked for by a search pick (FR5.3). */
  const [jumpRequest, setJumpRequest] = useState<{ refid: string; n: number } | null>(null);
  /*
   * A pick does what the row's name does (spec FR5): a written total opens in the editor — the
   * same `openEdit`, so the session switch and the stale-row guards are a click's — and an automatic
   * one, which has nothing to edit, only scrolls into view. Either way the list goes to the row.
   * Focus stays in the search box, as a pick leaves it on the wizard's other steps.
   */
  const pickTotal = useCallback(
    (hit: CompositeSearchHit) => {
      if (!hit.row.automatic) {
        setNotice(null);
        setError(null);
        openEdit(hit.row.refid, 'none');
      }
      setJumpRequest(j => ({ refid: hit.row.refid, n: (j?.n ?? 0) + 1 }));
    },
    [openEdit],
  );

  const closeSession = useCallback(() => {
    setSession(null);
    setArrival(null);
  }, []);

  /*
   * The announcement for the last change, finished once the list has re-derived the total: the
   * next render's `rows` ARE the patched plan's verdicts, so the state the chip now reads comes
   * from the same rows the list draws — no second verdict pass (spec Block A 4).
   */
  const pendingAnnouncement = useRef<{
    refid: string;
    said: string;
    was?: CompositeRowState;
  } | null>(null);
  useEffect(() => {
    const p = pendingAnnouncement.current;
    if (!p) return;
    pendingAnnouncement.current = null;
    const row = rows.find(r => r.refid === p.refid);
    const now = row ? rowState(row) : undefined;
    setNotice(
      now && now !== p.was ? `${p.said} The total now reads ${STATE_CHIP[now].text}.` : p.said,
    );
  }, [rows]);

  /**
   * The session's total as the STORE holds it now — R12 steps 1–3, shared by every write. Returns
   * the plan and the row to change, or `null` after saying why nothing may be written: the total
   * has gone (the panel closes), or it changed underneath (the session takes the stored row as
   * its new base, Revert's included, and is no longer offering to undo anything).
   */
  const storedForWrite = useCallback(
    (
      s: NonNullable<typeof session>,
    ): { current: FloorPlannerComposite[]; stored: FloorPlannerComposite } | null => {
      const current = asArray(latestComposites());
      const stored = current.find(c => c.refid === s.refid && isLive(c));
      if (!stored) {
        const label = s.lastWritten ? labelOfRef(s.lastWritten.target) : 'This total';
        setError(goneSentence(label));
        closeSession();
        addButtonIn(rootRef.current)?.focus();
        return null;
      }
      if (s.lastWritten && !sameStored(stored, s.lastWritten)) {
        setError(changedSentence(labelOfRef(stored.target)));
        setSession({
          ...s,
          lastWritten: stored,
          opened: s.mode === 'edit' ? stored : s.opened,
          wrote: false,
        });
        return null;
      }
      return { current, stored };
    },
    [closeSession, labelOfRef, latestComposites],
  );

  /**
   * **Apply one editor action to the stored total** (spec R12). Every change the panel makes comes
   * through here, in this order:
   *
   * 1. Read the plan AT WRITE TIME — never a render's copy.
   * 2. The total has gone (a background load replaced it, or it was deleted): write nothing, close
   *    the panel, say so.
   * 3. It changed since this session last wrote it (the page regenerated it, a load rewrote it):
   *    write nothing, say so, and take the stored row as the new base — the panel already shows it.
   * 4. Apply the change to the STORED row.
   * 5. A change that alters nothing writes nothing — which is what keeps a generated total
   *    generated: `storedShape` drops `generated`, so writing an unchanged total would freeze it.
   * 6. Write, and announce.
   *
   * Returns the row written, or `null` when nothing was.
   */
  const applyChange = useCallback(
    (
      change: (stored: FloorPlannerComposite) => FloorPlannerComposite,
      describe: (label: string, next: FloorPlannerComposite) => string,
    ): FloorPlannerComposite | null => {
      const s = sessionRef.current;
      if (!s?.refid) return null;
      const read = storedForWrite(s);
      if (!read) return null;
      const { current, stored } = read;
      const next = change(stored);
      /*
       * The only same-definition write that is still a change is Revert putting `generated` BACK
       * on a total the session made hand-made. Every other change is built by `storedShape`, which
       * never carries the flag, so writing it over an unchanged generated total would freeze it.
       */
      const restoresGenerated = next.generated === true && stored.generated !== true;
      if (sameDefinition(stored, fieldsOf(next)) && !restoresGenerated) {
        return null;
      }
      const patched = current.map(c => (c.refid === s.refid ? next : c));
      const before = rows.find(r => r.refid === s.refid);
      onChange(patched);
      setSession({ ...s, lastWritten: next, wrote: true });
      setError(null);
      pendingAnnouncement.current = {
        refid: next.refid,
        said: describe(labelOfRef(next.target), next),
        was: before ? rowState(before) : undefined,
      };
      return next;
    },
    [labelOfRef, onChange, rows, storedForWrite],
  );

  /** The ring on a card just added, and *Added* on the row it came from, for a moment. */
  useEffect(() => {
    if (!arrival) return;
    const timer = window.setTimeout(() => setArrival(null), LINK_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [arrival]);

  const addSource = useCallback(
    (ref: FloorPlannerCompositeRef, fromRow: string | null): boolean => {
      const written = applyChange(
        stored => withInputAdded(stored, ref),
        label => `Added ${labelOfRef(ref)} to the total for ${label}.`,
      );
      if (written) {
        const at = asArray(written.inputs).length - 1;
        setArrival({ card: `${at}:${encodeRef(ref)}`, row: fromRow });
      }
      return written !== null;
    },
    [applyChange, labelOfRef],
  );

  /** Picking a target (spec R13 in Add, R14 in Edit). */
  const pickTarget = useCallback(
    (value: string) => {
      const s = sessionRef.current;
      const ref = decodeRef(value);
      if (!s || !ref) return;
      // Read at pick time: a background load can file a total here before the render catches up,
      // and the write below reads the store, so the decision to open, create or move must too.
      const live = asArray(latestComposites()).filter(isLive);
      /*
       * What the pick does is decided by `targetPickOutcome` — the one function the field's
       * section labels also read, so *Already totalled · opens it* can never sit over a pick that
       * replaces (`composites-page-picker`, spec R1). Its rules are the editor's, unchanged:
       * re-targeting ONTO the exact target of a generated total while the page is generating
       * replaces it (spec R14, amended); any other written total on the target opens it.
       */
      const { outcome, found } = targetPickOutcome(verdictContext, value, {
        live,
        sessionRefid: s.refid,
        generating: generation?.active === true,
        automatic,
      });
      const existing = outcome === 'opens' ? found : undefined;
      if (existing) {
        setError(null);
        setNotice(`${labelOfRef(existing.target)} already has a total, so it is open to edit.`);
        openEdit(existing.refid);
        return;
      }
      if (s.mode === 'add' && !s.refid) {
        const made = prefillFor(layout, newCompositeRefid(), ref, observerCounts);
        onChange([...asArray(latestComposites()), made]);
        // In the list straight away, glowing in the just-changed mint.
        setRowGlow({ refid: made.refid, tone: 'new' });
        const filled = asArray(made.inputs).length > 0;
        setSession({
          ...s,
          refid: made.refid,
          lastWritten: made,
          prefilled: filled,
          replacedAutomatic: automatic.some(t => encodeRef(t.target) === value),
          wrote: true,
        });
        setError(null);
        setNotice(
          filled
            ? `Added the total for ${labelOfRef(ref)}, filled in from its child spaces.`
            : `Added the total for ${labelOfRef(ref)}.`,
        );
        return;
      }
      // R14: the same target is a change that alters nothing, and writes nothing.
      const moved = applyChange(
        stored => withTarget(stored, ref),
        label => `The total now totals ${label}.`,
      );
      // An Add session's hint and sub describe the target they were made for, not this one.
      if (moved && s.mode === 'add') {
        setSession(prev =>
          prev && prev.refid === moved.refid
            ? {
                ...prev,
                prefilled: false,
                replacedAutomatic: automatic.some(t => encodeRef(t.target) === value),
              }
            : prev,
        );
      }
    },
    [
      applyChange,
      automatic,
      generation?.active,
      labelOfRef,
      latestComposites,
      layout,
      observerCounts,
      onChange,
      openEdit,
      verdictContext,
    ],
  );

  /**
   * *Discard* — the new total goes, as if Add had never been pressed (spec R12). It was drawn this
   * session, so it is removed outright rather than tombstoned; the same two guards as any change
   * stop it removing a total that is no longer the one this session wrote.
   */
  const discard = useCallback(() => {
    const s = sessionRef.current;
    if (!s?.refid) {
      closeSession();
      return;
    }
    const read = storedForWrite(s);
    if (!read) return;
    const { current, stored } = read;
    const label = labelOfRef(stored.target);
    // Closed first, so the panel's own "this total has gone" check never sees its own discard.
    closeSession();
    onChange(current.filter(c => c.refid !== s.refid));
    setError(null);
    setNotice(`Discarded the new total for ${label}.`);
  }, [closeSession, labelOfRef, onChange, storedForWrite]);

  /*
   * The panel closes itself when the total it shows has gone — deleted from its row, or replaced by
   * the twin's own version when a background read landed — and says which.
   */
  useEffect(() => {
    if (!session?.refid || sessionRow) return;
    // The store, not a render: a total written a moment ago may not have reached this render yet.
    const refid = session.refid;
    if (asArray(latestComposites()).some(c => c.refid === refid && isLive(c))) return;
    const label = session.lastWritten ? labelOfRef(session.lastWritten.target) : 'This total';
    setError(goneSentence(label));
    closeSession();
  }, [closeSession, labelOfRef, latestComposites, session, sessionRow]);

  /**
   * The total the Reset/Delete confirm is asking about (spec R15), with what the dialog says about
   * it. Held as state rather than awaited: the coral `Modal` is the design system's confirm, in
   * place of the wizard's legacy `ConfirmDialog` (ruling 6 of the design-system reply).
   */
  const [pendingDelete, setPendingDelete] = useState<{
    refid: string;
    label: string;
    generated: boolean;
    /** It is generated again once deleted, so it reads as a reset (spec R16). */
    resets: boolean;
    sources: string;
    code?: string;
  } | null>(null);

  /**
   * Where focus goes when the confirm closes (spec R15, Block A 1): the row's Delete/Reset button
   * that opened it — still there after Keep or Esc — or, when that row has gone (a hand-made total
   * deleted, or replaced by a background read while the confirm was open), *Add composite measure*,
   * so focus never falls to the page. A getter, resolved by the `Modal` at close.
   */
  const deleteOpenerRef = useRef<HTMLElement | null>(null);
  const deleteReturnFocus = useMemo(
    () => ({
      get current(): HTMLElement | null {
        const opener = deleteOpenerRef.current;
        if (opener?.isConnected) return opener;
        return addButtonIn(rootRef.current);
      },
    }),
    [],
  );

  const askDelete = useCallback(
    (refid: string, opener?: HTMLElement | null) => {
      const row = live.find(c => c.refid === refid);
      if (!row) return;
      deleteOpenerRef.current =
        opener ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      // The panel never stays open on a total being deleted: it closes first, so its focus return
      // does not compete with the confirm's.
      if (sessionRef.current?.refid === refid) closeSession();
      const labelled = rows.find(r => r.refid === refid);
      setPendingDelete({
        refid,
        label: labelForRef(row.target, options).label,
        generated: row.generated === true,
        resets: labelled?.resets === true,
        sources: (labelled?.sources ?? []).map(source => source.label).join(', '),
        code: labelled?.code,
      });
    },
    [closeSession, live, options, rows],
  );

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    const { refid, label, generated: isGenerated, resets } = pendingDelete;
    setPendingDelete(null);

    /*
     * A measure LOADED from the running configuration is tombstoned rather than removed, and a
     * measure drawn this session is removed outright.
     *
     * The distinction is the re-match: the loaded set is re-applied on every open and on every
     * retry, so a loaded measure simply dropped from the array comes straight back. A tombstone
     * is what tells the carry-over the operator deleted it. A drawn measure has no loaded twin
     * to come back from, so there is nothing to tombstone.
     */
    /*
     * ⚠ Read the array at WRITE time, not from the closure captured before the dialog.
     *
     * The confirm is an unbounded wait on a human, and the plan's composites can be replaced while
     * it is open — the live-configuration match writes the same section when the statement read
     * lands. Writing back the captured array would discard that silently and for the session,
     * because the match's own guard stops it re-placing. `runPersistPlan` states the same rule
     * for the same reason: "a closure value can be a render stale".
     */
    const current = asArray(latestComposites());
    const row = current.find(c => c.refid === refid);
    if (!row) {
      setError(`${label} is no longer on this plan, so there was nothing to delete.`);
      return;
    }
    const isLoaded = refid.includes(LIVE_COMPOSITE_MARKER);
    const after = isLoaded
      ? current.map(c => (c.refid === refid ? { ...c, _deleted: true } : c))
      : current.filter(c => c.refid !== refid);
    onChange(after);
    setError(null);
    /*
     * Does it come straight back? A generated total exists only because its space qualified, and
     * deleting it changes nothing about that — a tombstone is not a hand-made total — so it comes
     * back exactly when the page is generating. While the configuration is still loading it comes
     * back later; while the read has failed it does not come back at all.
     */
    /*
     * A hand-made total on a space the page generates for comes back the same way — that is what
     * makes its delete a RESET (spec R16) — and says so in a reset's words. A generated total keeps
     * today's sentences word for word.
     */
    const lead = isGenerated ? `Deleted the total for ${label}.` : `Reset the total for ${label}.`;
    if (resets && generation?.active) {
      setNotice(`${lead} It has been generated again.`);
      setPendingFocus({ target: encodeRef(row.target), label, deletedRefid: refid });
    } else if (resets && !generation?.paused) {
      setNotice(`${lead} It will be generated again once the twin's running configuration loads.`);
      setPendingFocus({ target: '', label, deletedRefid: refid });
    } else {
      setNotice(resets ? lead : `Deleted the total for ${label}.`);
      if (resets) setPendingFocus({ target: '', label, deletedRefid: refid });
    }
  }, [generation?.active, generation?.paused, latestComposites, onChange, pendingDelete]);

  /*
   * FR10's announcement: the totals the session's first reconciliation added — on an adopted twin,
   * usually a couple of floors or zones its configuration was not adding up. Said once, through the
   * panel's polite notice region, when the stage is showing; the page holds it until then.
   */
  /*
   * Where keyboard focus goes after deleting a generated total (spec Block A, criterion 1). The
   * confirmation hands focus back to the delete button that opened it — which is gone: the row was
   * removed or tombstoned, and the total that comes back is a NEW row at the end of the list. So
   * focus moves to that row's delete button once it has rendered, and, where nothing comes back
   * yet, to "Add composite measure", so it never falls to the page.
   */
  useEffect(() => {
    if (!pendingFocus) return;
    const root = rootRef.current;
    if (!root) return;
    const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>('button'));
    // The regenerated total is a DIFFERENT row on the same target — never the one just deleted,
    // whose button can still be on screen for a render.
    const cameBackAs = live.find(
      c =>
        c.generated === true &&
        c.refid !== pendingFocus.deletedRefid &&
        encodeRef(c.target) === pendingFocus.target,
    );
    const cameBack = cameBackAs !== undefined;
    const regenerated =
      pendingFocus.target && cameBack
        ? buttons
            .filter(
              b =>
                b.getAttribute('aria-label') ===
                `Reset the generated total for ${pendingFocus.label}`,
            )
            .pop() // a regenerated total is appended, so it is the last such row
        : undefined;
    // Not back yet — wait for the next rows. Back but not drawn (its group is folded): no button
    // to land on, so *Add composite measure*, rather than waiting and stealing focus later.
    if (pendingFocus.target && !regenerated && !cameBack) return;
    // The total Reset put back arrives with the mint glow (the handoff: "reappears in place with
    // the Arrived look"; `composites-page-motion`, spec R7).
    if (pendingFocus.target && cameBackAs) setRowGlow({ refid: cameBackAs.refid, tone: 'arrived' });
    (regenerated ?? addButtonIn(root))?.focus();
    setPendingFocus(null);
  }, [live, pendingFocus, rows]);

  const added = generation?.added ?? null;
  const onAddedAnnounced = generation?.onAddedAnnounced;
  useEffect(() => {
    if (!added || added.length === 0) return;
    const labels = added.map(ref => labelForRef(ref, options).label);
    const n = labels.length;
    const shown = labels.slice(0, 3).join(', ');
    setNotice(
      `Added ${n} generated total${n === 1 ? '' : 's'} for spaces this twin was not adding up: ` +
        `${shown}${n > 3 ? `, +${n - 3} more` : ''}.`,
    );
    onAddedAnnounced?.();
  }, [added, onAddedAnnounced, options]);

  /** What each list row offers while the panel is open (spec R17). */
  const sourceAction = useMemo(
    () =>
      sourceActionsFor({
        rows,
        written: live,
        session: sessionRow ?? null,
        justAdded: arrival?.row ?? null,
        loops: key => (sessionRow ? wouldLoop(verdictContext, sessionRow, key) : false),
      }),
    [arrival?.row, live, rows, sessionRow, verdictContext],
  );

  /** *Add as source* on a list row: the row's target, or an automatic row's (spec R17). */
  const addSourceFromRow = useCallback(
    (rowRefid: string) => {
      const key = rowTargetKey(rowRefid, live);
      const ref = key ? decodeRef(key) : null;
      return ref ? addSource(ref, rowRefid) : false;
    },
    [addSource, live],
  );

  /*
   * The two pickers' options (`composites-page-picker`), each in its own memo so the editor's
   * option lists keep their identity across the panel's other changes (an arrival ring, a notice).
   * The source list is built whenever a total is open, not only while *Add source* is: the editor
   * owns that open state, and both builders together measure ~1.2 ms on PUL-CON (spec R15,
   * amendment 9).
   */
  const targetCandidates = useMemo(
    () =>
      targetPickerCandidates(verdictContext, {
        automatic,
        stateOfTotal,
        sessionRefid: sessionRow?.refid ?? null,
        currentTarget: sessionRow ? encodeRef(sessionRow.target) || null : null,
        generating: generation?.active === true,
      }),
    [automatic, generation?.active, sessionRow, stateOfTotal, verdictContext],
  );
  const sourceCandidates = useMemo(
    () => (sessionRow ? sourcePickerCandidates(verdictContext, sessionRow, lookups) : []),
    [lookups, sessionRow, verdictContext],
  );

  /** The panel, drawn from the plan (spec R1–R11). `null` while no session is open. */
  const editorProps = useMemo<CompositeMeasureEditorProps | null>(() => {
    if (!session) return null;
    const common = {
      sessionKey: String(session.n),
      focusOnOpen: session.focusOnOpen,
      returnFocusTo: editorReturnFocus,
      onDone: closeSession,
      onPickTarget: pickTarget,
    };

    // Add, before a target: the field and nothing else (board v2 frame 06).
    if (!session.refid || !sessionRow) {
      return {
        ...common,
        mode: 'add',
        title: 'Add composite measure',
        sub: 'Pick what it totals first',
        generatedNote: false,
        target: {
          value: null,
          options: targetCandidates,
        },
        targetNotes: [],
        sources: [],
        prefilled: false,
        sourceOptions: [],
        ownCameras: null,
        totalNotes: [],
        excluded: false,
        footer: { secondary: 'cancel' },
        onRemoveSource: () => undefined,
        onAddSource: () => undefined,
        onOwnCameras: () => undefined,
        onExcluded: () => undefined,
        onSecondary: closeSession,
        onDestructive: () => undefined,
      };
    }

    const stored = sessionRow;
    const refid = stored.refid;
    const targetKey = encodeRef(stored.target);
    const targetLabel = labelForRef(stored.target, options);
    const row = rows.find(r => r.refid === refid);
    const verdict = totalVerdicts(verdictContext, stored);
    const inputs = asArray(stored.inputs);
    const watching = observerCounts.get(targetKey) ?? 0;
    const counted = stored.includesOwnObservers === true;
    const ownState = ownCamerasState(watching, counted);
    const ownReason = ownCamerasReason(targetLabel.label, watching);

    const note = (kind: EditorNote['kind'], text: string | undefined): EditorNote[] =>
      text ? [{ kind, text }] : [];
    const distinct = (notes: EditorNote[]) =>
      notes.filter((n, i) => notes.findIndex(m => m.text === n.text) === i);

    // Every card carries every sentence that names its source, as soon as the panel opens (R8).
    const cards: EditorSource[] = (sources.get(refid) ?? [])
      .filter(source => source.state !== 'own-cameras' && source.key !== 'own-cameras')
      .map(source => {
        const at = Number(source.key.slice(0, source.key.indexOf(':')));
        return {
          key: source.key,
          label: source.label,
          kind: source.kind,
          code: codeOf(inputs[at]),
          state: source.state,
          text: source.text,
          notes: distinct([
            ...note('problem', verdict.errors.rows[at]),
            ...note('warning', verdict.warnings.rows[at]),
            ...note('warning', verdict.inputNotes[at]),
          ]),
          arriving: arrival?.card === source.key,
        };
      });

    const targetNotes = distinct([
      ...(verdict.errors.target && verdict.errors.target !== ownReason
        ? note('problem', verdict.errors.target)
        : []),
      ...(reviews.get(refid) ?? []).map(text => ({ kind: 'review' as const, text })),
      ...verdict.targetNotes.map(text => ({ kind: 'warning' as const, text })),
    ]);

    const emptyNote =
      verdict.warnings.members === EMPTY_TOTAL_WARNING ? EMPTY_TOTAL_WARNING : undefined;
    const totalNotes = distinct([
      ...note('problem', verdict.errors.members),
      ...(emptyNote ? [] : note('warning', verdict.warnings.members)),
    ]);

    // The target field's sections (`composites-page-picker`, R1–R4), plus this total's own target if
    // the plan lost it — listed first, unpickable.
    const picks: PickerCandidate[] = [...targetCandidates];
    if (targetKey && !picks.some(o => o.value === targetKey)) {
      picks.unshift({
        value: targetKey,
        label: targetLabel.label,
        kind: targetKindOf(stored.target),
        state: 'gone',
        text: 'No longer on the plan',
        disabled: true,
      });
    }
    // *Add source*: what it does not already add up and could not loop through (R6–R8).
    const sourceOptions = sourceCandidates;

    const code = codeOf(stored.target);
    const isAdd = session.mode === 'add';
    const sub = targetLabel.unresolved
      ? 'no longer on the plan'
      : isAdd
        ? session.replacedAutomatic
          ? 'New total · replaces its automatic total'
          : 'New total'
        : stored.generated === true
          ? `Generated total${code ? ` · ${code}` : ''}`
          : code;

    const opened = session.opened;
    const revertEnabled =
      session.wrote &&
      !!opened &&
      (!sameStored(stored, opened) || (stored.generated === true) !== (opened.generated === true));

    return {
      ...common,
      mode: session.mode,
      title: targetLabel.label,
      sub,
      state: row ? rowState(row) : undefined,
      generatedNote: opened?.generated === true,
      target: { value: targetKey, options: picks },
      targetNotes,
      sources: cards,
      prefilled: session.prefilled,
      sourceOptions,
      ownCameras: {
        state: ownState,
        watching,
        targetLabel: targetLabel.label,
        // Locked carries the rule as its explanation. Required carries it as a refusal, so only when
        // the verdict says it: the rules keep the first target error (a duplicate) and drop this
        // one, and the list and Launch must not be out-said by the panel (Copilot, PR #1928).
        reason:
          ownState === 'locked' || (ownState === 'required' && verdict.errors.target === ownReason)
            ? ownReason
            : undefined,
      },
      totalNotes,
      emptyNote,
      excluded: stored.excluded === true,
      footer: {
        destructive: row?.resets ? 'reset' : 'delete',
        secondary: isAdd ? 'discard' : 'revert',
        revertEnabled,
      },
      onRemoveSource: key => {
        const at = Number(key.slice(0, key.indexOf(':')));
        const gone = inputs[at];
        if (gone === undefined) return;
        applyChange(
          s => withInputRemoved(s, at),
          label => `Removed ${labelOfRef(gone)} from the total for ${label}.`,
        );
      },
      onAddSource: value => {
        const ref = decodeRef(value);
        if (ref) addSource(ref, null);
      },
      onOwnCameras: on =>
        applyChange(
          s => withOwnCameras(s, on),
          label =>
            on
              ? `Now counting ${label}’s own cameras.`
              : `No longer counting ${label}’s own cameras.`,
        ),
      onExcluded: on =>
        applyChange(
          s => withExcluded(s, on),
          label =>
            on
              ? `${label} is excluded from processing.`
              : `${label} is no longer excluded from processing.`,
        ),
      onSecondary: () => {
        if (!isAdd) {
          if (opened)
            applyChange(
              () => opened,
              label => `Reverted the total for ${label}.`,
            );
          return;
        }
        discard();
      },
      onDestructive: () => {
        askDelete(refid, editButtonIn(rootRef.current, refid) ?? addButtonIn(rootRef.current));
      },
    };
  }, [
    addSource,
    applyChange,
    arrival?.card,
    askDelete,
    closeSession,
    codeOf,
    discard,
    editorReturnFocus,
    labelOfRef,
    observerCounts,
    options,
    pickTarget,
    reviews,
    rows,
    session,
    sessionRow,
    sourceCandidates,
    sources,
    targetCandidates,
    verdictContext,
  ]);

  return (
    <div className={className} ref={rootRef}>
      {searchSlot &&
        createPortal(
          <CompositeTotalSearch
            match={matchTotals}
            noTotals={rows.length === 0}
            readState={totalsReadState(liveConfiguration)}
            onPick={pickTotal}
            onLoadTypes={searchIndex.loadTypes}
            typeStatus={searchIndex.typeStatus}
          />,
          searchSlot,
        )}
      {liveConfiguration && <LiveReadNotices {...liveConfiguration} />}

      {/*
       * The list, then a reserved column the width of the editor panel (`DockedCard` `beside`, 448),
       * whether or not anything is open — so the list never resizes when the editor arrives
       * (spec R3; board v2: list right edge at 472 in every frame).
       */}
      <div className="composites-body">
        <div className="list-column">
          <CompositeMeasuresPanel
            composites={rows}
            notice={notice}
            error={error}
            selectedRefid={session?.refid ?? null}
            editingOpen={session !== null}
            sourceAction={sourceAction}
            onAdd={handleAdd}
            onEdit={handleEdit}
            onDelete={askDelete}
            onAddSource={addSourceFromRow}
            rowGlow={rowGlow}
            motionEpoch={motionEpoch}
            jumpRequest={jumpRequest}
          />
          {/*
           * Under the list, in a region that is ALWAYS mounted with the condition inside it: a live
           * region that appears at the moment it has something to say is announced to nobody, and the
           * read can fail after the stage is already showing (spec Failure States and Block A, 4).
           */}
          <div className="paused-region" aria-live="polite" aria-atomic="true">
            {generation?.paused && (
              <Banner
                surface="on-glass"
                tone="info"
                title="Generated totals are paused until the twin's running configuration loads."
              />
            )}
          </div>
        </div>
        <div className="editor-column">
          {editorProps ? <CompositeMeasureEditor {...editorProps} /> : null}
        </div>
      </div>
      {pendingDelete && (
        /*
         * Reset or Delete (spec R15, board v2 frame 08): the design system's coral confirm, 440
         * wide, as the Observers page's Remove Camera. Keep is focused first, and focus goes back to
         * the row's button on Keep (the Modal returns it to whatever opened it).
         */
        <Modal
          open
          onClose={() => setPendingDelete(null)}
          returnFocusTo={deleteReturnFocus}
          title={`${pendingDelete.resets ? 'Reset' : 'Delete'} the total for ${pendingDelete.label}?`}
          tone="coral"
          width="md"
          icon={<Icon name={pendingDelete.resets ? 'rotate-left' : 'trash-can'} />}
          actions={
            <>
              <Button
                type="button"
                variant="frosted"
                autoFocus
                onClick={() => setPendingDelete(null)}
              >
                Keep Total
              </Button>
              <Button
                type="button"
                variant="destructive"
                iconLeft={<Icon name={pendingDelete.resets ? 'rotate-left' : 'trash-can'} />}
                onClick={confirmDelete}
              >
                {pendingDelete.resets ? 'Reset Total' : 'Delete Total'}
              </Button>
            </>
          }
        >
          <DeleteBody>
            {/*
             * Today's sentences, word for word. A generated total comes straight back while its
             * space has child spaces (Rob, 2026-10-01), so the dialog says so — and what to do
             * instead if a different total is wanted there.
             */}
            <p>
              {pendingDelete.generated
                ? `Delete the generated total for ${pendingDelete.label}? It is generated again ` +
                  `while ${pendingDelete.label} has child spaces. To keep a different total, edit ` +
                  'it instead.'
                : pendingDelete.resets
                  ? `Reset the total for ${pendingDelete.label}? It is replaced by the total ` +
                    `generated from ${pendingDelete.label}'s child spaces.`
                  : `Delete the total for ${pendingDelete.label}? It is removed from the plan. The ` +
                    'twin keeps measuring it until you take the twin live.'}
            </p>
            <div className="summary">
              <span>
                {pendingDelete.label}
                {pendingDelete.sources ? ` · adds up ${pendingDelete.sources}` : ''}
              </span>
              {pendingDelete.code ? (
                <Tag surface="on-solid" tone="code">
                  {pendingDelete.code}
                </Tag>
              ) : null}
            </div>
          </DeleteBody>
        </Modal>
      )}
    </div>
  );
};

/** The confirm's body: today's sentence, then the total and what it adds up, its BID at the right. */
const DeleteBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};

  p {
    margin: 0;
  }

  .summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space[2]};
    color: ${ink[2]};
    font-size: ${typeScale.caption1.fontSize};
    line-height: ${typeScale.caption1.lineHeight};
  }
`;

export default styled(OnboardingCompositeMeasures)`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .live-read-notices:empty {
    display: none;
  }

  .live-read-notices {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: ${space[2]};
    padding: ${space[3]} ${space[3]} 0;
  }

  /* Board v2: the list, a 12 px gap, then the 448 px editor column; 12 px gutters all round. */
  .composites-body {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 448px;
    gap: ${space[3]};
    padding: ${space[3]};
    isolation: isolate;
  }

  .list-column {
    display: flex;
    flex-direction: column;
    gap: ${space[3]};
    min-height: 0;
    min-width: 0;
  }

  .list-column > :first-child {
    flex: 1;
    min-height: 0;
  }

  /* Kept in the accessibility tree while empty (never display none); the negative margin only
     gives back the column's gap, so an empty region takes no room. */
  .paused-region:empty {
    margin-top: -${space[3]};
  }

  .unplaced-list {
    margin: ${space[2]} 0;
    padding-left: ${space[5]};
  }
`;
